import { useEffect, useRef, useState } from 'react';

import { CloseRounded } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Typography,
} from '@mui/material';
import { green } from '@mui/material/colors';

import {
  IIPENSessionData,
  ISessionSigner,
} from '../../../../types/IPENSessionData';
import { ISigner } from '../../../../types/Parties';
import { successGreen } from '../../../constants/colors';
import {
  SignerType,
  isBorrower,
  isNPE,
  isSignerOfType,
} from '../../../constants/solexSignerTypes';
import { IIPENNotaryState } from '../../../redux-store/ipenNotary/ipenNotaryTypes';
import IPENSessionNotary from '../../IPENSessionNotary';
import IPENSessionSigner from '../../IPENSessionSigner';
import NotaryInfo from '../../NotaryInfo';
import QRModal from '../QRModal';

interface IProps {
  sessionData: IIPENSessionData;
  open: boolean;
  ipenNotary: IIPENNotaryState['notary'];
  sessionLaunching?: boolean;
  notaryInfoConfirmed?: boolean;
  isFetchingNotary?: boolean;
  ipenSession: any;
  close: () => void;
  confirmNotaryInfo: () => void;
  unlockParty: (partyId: string) => any;
  getSsoToken: (arg: string) => any;
  activateSession: (partyId: string) => any;
  editNotaryRegistration: () => void;
}

type DelegatedSessionSigner = ISessionSigner & { signingDelegator?: ISigner };

export default function IPENSessionModal(props: IProps) {
  const {
    sessionData,
    open,
    close,
    ipenNotary,
    sessionLaunching,
    confirmNotaryInfo,
    notaryInfoConfirmed,
    unlockParty,
    getSsoToken,
    activateSession,
    isFetchingNotary,
    editNotaryRegistration,
    ipenSession,
  } = props;

  //TOOD SXA-3245: Will have this when we want to not show ipen carousel
  //const [cookies, setCookie] = useCookies(['viewed_ipensession']);
  const form: any = useRef(null);
  const input: any = useRef(null);

  const [notaryAccessToken, setNotaryAccessToken] = useState<string>('');
  const [targetSigner, setTargetSigner] = useState<ISessionSigner | null>(null);
  const [openQRModal, setOpenQRModal] = useState<boolean>(false);
  const [loadingPartyIds, setLoadingPartyIds] = useState<string[]>([]);
  const [isFetchingToken, setIsFetchingToken] = useState<boolean>(false);

  const replaceWithSigningDelegateIfApplicableFn =
    (): ((signer: ISessionSigner) => DelegatedSessionSigner) =>
    (signer: ISessionSigner) => {
      if (signer.signer.SigningDelegatePartyId) {
        const delegateSigner = sessionData.sessionSigners.find(
          (s) => s.signer.Id === signer.signer.SigningDelegatePartyId
        );
        return delegateSigner
          ? { ...delegateSigner, signingDelegator: signer.signer }
          : signer;
      }

      return signer;
    };

  const borrowerSigners: DelegatedSessionSigner[] = sessionData?.sessionSigners
    .filter((sessionSigner) => isBorrower(sessionSigner.signer))
    .map(replaceWithSigningDelegateIfApplicableFn());

  const npeSigners: DelegatedSessionSigner[] = sessionData?.sessionSigners
    .filter((sessionSigner) => isNPE(sessionSigner.signer))
    .map(replaceWithSigningDelegateIfApplicableFn());

  const witnessSigners: ISessionSigner[] = sessionData?.sessionSigners.filter(
    (sessionSigner) => isSignerOfType(sessionSigner.signer, SignerType.Witness)
  );

  const haveInitialSignersSigned = (): boolean => {
    const initialSigners: ISessionSigner[] = [
      ...(borrowerSigners ?? []),
      ...(npeSigners ?? []),
    ];
    return initialSigners.length
      ? initialSigners.every((signer) => signer.completed)
      : true;
  };

  const resetTarget = () => {
    setOpenQRModal(false);
    setTargetSigner(null);
  };

  const signDocs = (docLink: string | undefined) => {
    if (docLink) {
      window.open(docLink, '_blank');
    }
  };

  const viewQR = (target: ISessionSigner) => {
    setTargetSigner(target);
    setOpenQRModal(true);
  };

  const openPartyLink = (): string | undefined => {
    return sessionData.notaryLink;
  };

  const closeModal = () => {
    setNotaryAccessToken('');
    close();
  };

  const openNotaryLink = () => {
    closeModal();
    form.current.submit();
  };

  // useEffect(() => {
  //   if (open) {
  //     fetchNotary()
  //     //TODO SXA-3245: Comment out this section to Persist Doc Editor and Launch Notary Session Carousels each time the user clicks the action
  //     //This will be back after a couple month when user get use to the new application
  //     // const viewedIPENSession = cookies['viewed_ipensession']
  //     // if (viewedIPENSession) {
  //     //   setShowIPENOverview(false)
  //     //   fetchNotary()
  //     // } else {
  //     //   setShowIPENOverview(true)
  //     //   setCookie('viewed_ipensession', 'true')
  //     // }
  //   }
  // }, [open])

  // useEffect for updating Notary AccessToken
  useEffect(() => {
    const fetchNotaryAccessToken = async () => {
      setIsFetchingToken(true);
      const partyLink = openPartyLink();
      if (partyLink && sessionData.allSignersSigned) {
        getSsoToken(partyLink).then((response: any) => {
          if (response && response.payload.length > 0) {
            setNotaryAccessToken(response.payload);
            setIsFetchingToken(false);
          }
        });
      }
    };
    if (open && sessionData.allSignersSigned && notaryAccessToken === '') {
      fetchNotaryAccessToken();
    }
  }, [open, sessionData]);

  useEffect(() => {
    if (haveInitialSignersSigned()) {
      const unlockWitnessSessionPromises = (
        witnessSigners?.filter((witnessSigner) => {
          const isLoading = loadingPartyIds.some(
            (id) => id === witnessSigner.signer.Id
          );
          return (
            !isLoading && !isSignerIpenSessionActive(witnessSigner.signer.Id)
          );
        }) ?? []
      ).map((witnessSigner) => handleUnlockSession(witnessSigner.signer.Id));

      void Promise.all(unlockWitnessSessionPromises);
    }
  }, [sessionData]);

  const isSignerIpenSessionActive = (signerId: string): boolean =>
    ipenSession?.activeSignerIds.some((id: string) => id === signerId);

  const handleUnlockParty = (Id: string) => {
    if (Id) {
      setLoadingPartyIds((prev) => [...prev, Id]);
      unlockParty(Id).then(() => {
        setLoadingPartyIds((prev) => prev.filter((prevId) => prevId !== Id));
      });
    }
  };

  const handleUnlockSession = async (Id: string) => {
    setLoadingPartyIds((prev) => [...prev, Id]);
    activateSession(Id).then(() => {
      setLoadingPartyIds((prev) => prev.filter((prevId) => prevId !== Id));
    });
  };

  return (
    <>
      <QRModal
        open={openQRModal && targetSigner ? true : false}
        onClose={() => resetTarget()}
        value={targetSigner && targetSigner.docLink ? targetSigner.docLink : ''}
      />

      <Dialog
        fullWidth={true}
        maxWidth='xl'
        sx={{
          width: '100%',
          '.MuiBackdrop-root': {
            backgroundColor: 'rgb(255 255 255 / 90%)',
          },
          '& .MuiDialog-container': {
            alignItems: 'flex-start',
          },
        }}
        open={open}
        scroll={'paper'}>
        {!sessionLaunching && (
          <DialogTitle
            sx={{
              backgroundColor:
                !sessionLaunching && notaryInfoConfirmed
                  ? successGreen
                  : 'white',
              color:
                !sessionLaunching && notaryInfoConfirmed ? 'white' : 'inherit',
            }}>
            <Grid
              container
              justifyContent={'space-between'}
              alignItems={'center'}>
              <Grid item sx={{ flexGrow: 1, flexShrink: 0 }}>
                {!notaryInfoConfirmed && !sessionLaunching
                  ? 'Confirm Notary Information'
                  : 'IPEN SESSION IN PROGRESS'}
              </Grid>
              {!sessionLaunching && !isFetchingNotary && (
                <Grid item sx={{ flexGrow: 0, flexShrink: 1 }}>
                  <IconButton
                    color='inherit'
                    onClick={() => closeModal()}
                    component='button'>
                    <CloseRounded fontSize='inherit' />
                  </IconButton>
                </Grid>
              )}
            </Grid>
          </DialogTitle>
        )}

        {sessionLaunching && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              minHeight: '40vh',
              justifyContent: 'center',
              alignItems: 'center',
            }}>
            <CircularProgress />
            <Typography
              variant='h6'
              sx={{ paddingTop: (theme) => theme.spacing(2) }}>
              Launching IPEN Session...
            </Typography>
            <Typography
              variant='subtitle1'
              sx={{
                color: (theme) => theme.palette.grey[600],
                paddingTop: (theme) => theme.spacing(2),
              }}>
              Please be patient while we confirm and launch your session.
            </Typography>
          </Box>
        )}

        {!notaryInfoConfirmed && !sessionLaunching && (
          <>
            <DialogContent dividers>
              {isFetchingNotary ? (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: '40vh',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}>
                  <CircularProgress />
                  <Typography
                    variant='h6'
                    sx={{ paddingTop: (theme) => theme.spacing(2) }}>
                    Fetching Notary Details...
                  </Typography>
                </Box>
              ) : (
                <NotaryInfo notary={ipenNotary} />
              )}
            </DialogContent>
            {!isFetchingNotary && (
              <DialogActions sx={{ justifyContent: 'space-between' }}>
                <Button
                  onClick={editNotaryRegistration}
                  variant='contained'
                  color='primary'
                  disableElevation>
                  Edit Profile
                </Button>
                <Button
                  onClick={() => confirmNotaryInfo()}
                  variant='contained'
                  sx={{ background: green[500] }}
                  disableElevation>
                  Confirm & Launch Session
                </Button>
              </DialogActions>
            )}
          </>
        )}

        {!sessionLaunching && notaryInfoConfirmed && (
          <>
            <DialogContent dividers>
              <Box
                sx={{
                  width: '100%',
                  background: '#F9F9F9',
                  display: 'flex',
                  flexDirection: 'column',
                  flexGrow: 1,
                }}>
                <Alert
                  severity='info'
                  sx={{
                    mx: 'auto',
                    margin: (theme) => theme.spacing(2),
                  }}>
                  Begin the signing session. All active sessions may sign
                  concurrently.
                </Alert>
                {!!borrowerSigners.length && (
                  <Typography
                    sx={{ margin: (theme) => ` 0px ${theme.spacing(2)}` }}
                    variant='button'>
                    {borrowerSigners.length > 1 ? 'Borrowers' : 'Borrower'}
                  </Typography>
                )}
                {borrowerSigners.map(
                  (borrowerSigner: DelegatedSessionSigner, index) => (
                    <IPENSessionSigner
                      key={index}
                      signer={borrowerSigner.signer}
                      signingDelegator={borrowerSigner.signingDelegator}
                      completed={borrowerSigner.completed}
                      signingPrerequisitesMet={true}
                      sessionActive={ipenSession?.activeSignerIds.some(
                        (id: string) =>
                          borrowerSigner.signer.Id?.toString() === id
                      )}
                      linkDisabled={borrowerSigner.linkDisabled}
                      loading={loadingPartyIds.some(
                        (id) => id === borrowerSigner.signer.Id?.toString()
                      )}
                      viewQR={() => viewQR(borrowerSigner)}
                      activateSession={() =>
                        handleUnlockSession(borrowerSigner.signer.Id)
                      }
                      signDocs={() => signDocs(borrowerSigner.docLink)}
                      unlockParty={() =>
                        handleUnlockParty(borrowerSigner.signer.Id)
                      }
                    />
                  )
                )}
                {!!npeSigners.length && (
                  <Typography
                    sx={{ margin: (theme) => ` 0px ${theme.spacing(2)}` }}
                    variant='button'>
                    {npeSigners.length > 1 ? 'NPEs' : 'NPE'}
                  </Typography>
                )}
                {npeSigners.map((npeSigner: DelegatedSessionSigner, index) => (
                  <IPENSessionSigner
                    key={index}
                    signer={npeSigner.signer}
                    signingDelegator={npeSigner.signingDelegator}
                    signingPrerequisitesMet={true}
                    completed={npeSigner.completed}
                    sessionActive={ipenSession?.activeSignerIds.some(
                      (id: string) => npeSigner.signer.Id?.toString() === id
                    )}
                    linkDisabled={npeSigner.linkDisabled}
                    loading={loadingPartyIds.some(
                      (id) => id === npeSigner.signer.Id?.toString()
                    )}
                    viewQR={() => viewQR(npeSigner)}
                    activateSession={() =>
                      handleUnlockSession(npeSigner.signer.Id)
                    }
                    signDocs={() => signDocs(npeSigner.docLink)}
                    unlockParty={() => handleUnlockParty(npeSigner.signer.Id)}
                  />
                ))}
                {!!witnessSigners.length && (
                  <Typography
                    sx={{ margin: (theme) => ` 0px ${theme.spacing(2)}` }}
                    variant='button'>
                    {witnessSigners.length > 1 ? 'Witnesses' : 'Witness'}
                  </Typography>
                )}
                {witnessSigners.map((witnessSigner: ISessionSigner, index) => (
                  <IPENSessionSigner
                    key={index}
                    signer={witnessSigner.signer}
                    completed={witnessSigner.completed}
                    signingPrerequisitesMet={haveInitialSignersSigned()}
                    sessionActive={ipenSession?.activeSignerIds.some(
                      (id: string) => witnessSigner.signer.Id?.toString() === id
                    )}
                    linkDisabled={witnessSigner.linkDisabled}
                    loading={loadingPartyIds.some(
                      (id) => id === witnessSigner.signer.Id?.toString()
                    )}
                    viewQR={() => viewQR(witnessSigner)}
                    activateSession={() =>
                      handleUnlockSession(witnessSigner.signer.Id)
                    }
                    signDocs={() => signDocs(witnessSigner.docLink)}
                    unlockParty={() =>
                      handleUnlockParty(witnessSigner.signer.Id)
                    }
                  />
                ))}
                {sessionData.notary && (
                  <>
                    <Typography
                      sx={{ margin: (theme) => ` 0px ${theme.spacing(2)}` }}
                      variant='button'>
                      Notary
                    </Typography>
                    <IPENSessionNotary
                      notary={sessionData.notary}
                      loading={isFetchingToken}
                      linkDisabled={!sessionData.allSignersSigned}
                      openLink={() => openNotaryLink()}
                    />
                  </>
                )}
              </Box>
            </DialogContent>

            <form
              ref={form}
              target='_blank'
              method='POST'
              action={openPartyLink()}>
              <input
                ref={input}
                type='hidden'
                name='access_token'
                value={notaryAccessToken}
              />
            </form>
          </>
        )}
      </Dialog>
    </>
  );
}
